import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { viewAsTestPerson } from '@/lib/viewAs'

type TestPerson = { id: string; name: string; email: string; circles: number }

// The owner's pretend people, for trying gatheredfrom from someone else's side. They live apart from everyone real:
// only test people can be in a circle a test person starts, and they can't join a real one (see worker/testPeople.ts).
export function TestPeople() {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const people = useQuery({
    queryKey: ['test-people'],
    queryFn: () => api<{ people: TestPerson[] }>('/admin/test-people'),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['test-people'] })
  const add = useMutation({
    mutationFn: (name: string) => api('/admin/test-people', { method: 'POST', body: { name } }),
    onSuccess: () => {
      setName('')
      return refresh()
    },
  })
  const view = useMutation({ mutationFn: viewAsTestPerson })
  const clear = useMutation({
    mutationFn: () => api('/admin/test-people', { method: 'DELETE' }),
    onSettled: refresh,
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    add.mutate(name.trim())
  }

  const list = people.data?.people ?? []
  return (
    <section className="grid min-w-0 gap-3" aria-labelledby="test-people">
      <div className="flex flex-col gap-1.5">
        <h3 id="test-people" className="text-sm font-medium">
          Test people
        </h3>
        <p className="text-sub text-muted-foreground">
          Pretend people for trying things out. View the app as one of them to start a test circle, invite another test
          person by the address shown here, then view as them to see it from their side. They never meet anyone real,
          never get email, and can’t sign in.
        </p>
      </div>
      <form onSubmit={submit} className="grid gap-2">
        <Label htmlFor="test-person-name">Add a test person</Label>
        <div className="flex gap-2">
          <Input id="test-person-name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={add.isPending}>
            Add
          </Button>
        </div>
        <FormError error={add.error} />
      </form>
      {list.length > 0 && (
        <ul className="flex flex-col divide-y divide-border/70">
          {list.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2">
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{p.name}</span>
                <span className="truncate text-sub text-muted-foreground select-all">{p.email}</span>
                <span className="text-sub text-muted-foreground">
                  {p.circles === 0 ? 'No circles yet' : p.circles === 1 ? 'In 1 circle' : `In ${p.circles} circles`}
                </span>
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={view.isPending}
                onClick={() => view.mutate(p.id)}
                aria-label={`View as ${p.name}`}
              >
                View as
              </Button>
            </li>
          ))}
        </ul>
      )}
      <FormError error={view.error ?? clear.error} />
      {list.length > 0 && (
        <ConfirmButton
          label="Delete all test people"
          title="Delete all test people?"
          description="Every test person and every test circle goes, with everything in them. Nothing real is touched."
          confirm="Delete them"
          disabled={clear.isPending}
          onConfirm={() => clear.mutate()}
        />
      )}
    </section>
  )
}
